/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import React, { useState } from 'react'

import { ActivityCarousel } from '@/components/quality-explorer/carousel/ActivityCarousel'
import { FilterableKit, kitFilters } from '@/components/quality-explorer/carousel/activity-carousel-filters'
import { Activity } from '@/components/quality-explorer/types'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ActivityCarouselProps = {
  /** The kit to show the filters for. */
  kit: FilterableKit

  /** The activities to display in the carousel. */
  activities: Array<Activity>
}

/**
 * A wrapper for the activity carousel that includes the filter cards for the specific kits.
 */
const ActivityCarouselWithFilters: React.FC<ActivityCarouselProps> = ({ kit, activities }) => {
  const [filteredActivities, setActivities] = useState<Array<Activity>>(activities)
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const filters = kitFilters[kit]

  return (
    <div className={'relative w-full max-w-7xl mx-auto px-4 py-8'}>
      <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'}>
        {filters.map((filter) => {
          const Icon = filter.icon
          return (
            <div
              key={filter.title}
              className={cn('cursor-pointer h-full')}
              onClick={() => {
                if (selectedFilter === filter.title) {
                  setActivities(activities)
                  setSelectedFilter(null)
                } else {
                  setActivities(activities.filter((activity) => filter.activities.includes(activity.alias_p)))
                  setSelectedFilter(filter.title)
                }
              }}
            >
              <div
                className={cn(
                  'h-full flex flex-col border-4 border-transparent shadow-mta p-4 rounded-2xl hover:shadow-lg',
                  selectedFilter === filter.title && 'border-secondary-500 bg-secondary-100',
                )}
              >
                <div className={'flex flex-row items-center gap-4'}>
                  <div className={'flex items-center justify-center w-12 h-12 bg-secondary-500 rounded-full shadow-lg'}>
                    <Icon />
                  </div>
                  <h3 className={'text-xl text-primary-500 font-semibold'}>{filter.title}</h3>
                </div>
                <p className={'mt-4 text-sm text-graphite-500'}>{filter.description}</p>
              </div>
            </div>
          )
        })}
      </div>
      <ActivityCarousel activities={filteredActivities} />
    </div>
  )
}

export { ActivityCarouselWithFilters }
